import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { API } from "@/lib/api";

export default function PaymentResult({ variant }) {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState(variant === "success" ? "verifying" : "cancelled");

  useEffect(() => {
    if (variant !== "success" || !sessionId) return;
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
      } catch (e) {
        // keep polling briefly
      }
      attempts += 1;
      if (attempts < 15) setTimeout(poll, 2000);
      else setState("pending");
    };
    poll();
  }, [variant, sessionId]);

  const content = {
    verifying: { icon: Loader2, spin: true, title: "Confirming your payment…", body: "Hold on a moment while we verify with Stripe.", testid: "payment-verifying" },
    paid: { icon: CheckCircle2, title: "Payment confirmed.", body: "Your Dual Care subscription is active. A receipt is on its way to your inbox — welcome to calmer co-parenting.", testid: "payment-success-message" },
    pending: { icon: Loader2, spin: true, title: "Still processing", body: "Stripe is taking a little longer than usual. Your subscription will activate automatically — no need to pay again.", testid: "payment-pending-message" },
    cancelled: { icon: XCircle, title: "Checkout cancelled.", body: "No charge was made. Whenever you're ready, the plans will be waiting.", testid: "payment-cancel-message" },
  }[state];

  return (
    <main className="min-h-screen flex items-center justify-center bg-paper px-6" data-testid={`payment-${variant}-page`}>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="max-w-md w-full rounded-3xl border border-ink/10 bg-paper-2 p-10 text-center"
      >
        <content.icon className={`mx-auto h-12 w-12 ${state === "cancelled" ? "text-clay" : "text-sage"} ${content.spin ? "animate-spin" : ""}`} />
        <h1 className="mt-6 font-serif text-3xl font-semibold tracking-tight" data-testid={content.testid}>{content.title}</h1>
        <p className="mt-4 text-sm text-ink-muted leading-relaxed">{content.body}</p>
        <Link
          to="/"
          data-testid="payment-back-home-btn"
          className="mt-8 inline-block rounded-full bg-ink px-7 py-3 text-sm font-medium text-paper transition-colors duration-300 hover:bg-clay"
        >
          Back to Dual Care
        </Link>
      </motion.div>
    </main>
  );
}
